import { useId, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  QuizAnswer,
  QuizAttemptResult,
  QuizForLearner,
  QuizQuestionForLearner,
  QuizQuestionResult,
} from '@lp/shared';
import { Check, RotateCcw, X } from 'lucide-react';
import { api } from '../../lib/api.js';
import { formatDateTime, pct } from '../../lib/format.js';
import { learnerKeys, useQuizAttempts } from '../../lib/useLearnerData.js';
import { useToast } from '../Toast.js';
import { Markdown } from '../Markdown.js';
import { Alert, Badge, Button, Card, CardHeader, Input, clsx } from '../ui.js';

/**
 * The knowledge check.
 *
 * This is feedback, never a grade: the threshold is described as a suggestion,
 * the score is never called a result of record, and nothing about it is
 * submitted to anyone. Answers stay hidden until the learner submits — the API
 * does not send `correctOptionIds` or the explanation before then, and this
 * component does not invent them.
 */
export function QuizRunner({
  quiz,
  sessionId,
}: {
  quiz: QuizForLearner;
  sessionId: string;
}) {
  const queryClient = useQueryClient();
  const { toastError } = useToast();
  const [answers, setAnswers] = useState<Record<string, QuizAnswer>>({});
  const [result, setResult] = useState<QuizAttemptResult | null>(null);
  const history = useQuizAttempts(sessionId, true);

  const outOfAttempts = quiz.attemptsRemaining !== null && quiz.attemptsRemaining <= 0;

  const submit = useMutation({
    mutationFn: () => {
      const payload = quiz.questions.map<QuizAnswer>((question) => {
        const chosen = answers[question.id];
        return {
          questionId: question.id,
          optionIds: chosen?.optionIds ?? [],
          text: chosen?.text ?? null,
        };
      });
      return api.post<QuizAttemptResult>(
        `/sessions/${encodeURIComponent(sessionId)}/quiz/attempts`,
        { answers: payload },
      );
    },
    onSuccess: (data) => {
      setResult(data);
      void queryClient.invalidateQueries({ queryKey: learnerKeys.quizAttempts(sessionId) });
    },
    onError: (error) => toastError(error, 'That attempt could not be recorded.'),
  });

  const resultsByQuestion = useMemo(() => {
    const map = new Map<string, QuizQuestionResult>();
    for (const item of result?.results ?? []) map.set(item.questionId, item);
    return map;
  }, [result]);

  const answeredCount = quiz.questions.filter((question) => {
    const answer = answers[question.id];
    if (!answer) return false;
    return question.kind === 'short_answer'
      ? !!answer.text?.trim()
      : answer.optionIds.length > 0;
  }).length;

  const setChoice = (question: QuizQuestionForLearner, optionId: string): void => {
    setAnswers((prev) => {
      const current = prev[question.id]?.optionIds ?? [];
      const next =
        question.kind === 'multi_select'
          ? current.includes(optionId)
            ? current.filter((id) => id !== optionId)
            : [...current, optionId]
          : [optionId];
      return {
        ...prev,
        [question.id]: { questionId: question.id, optionIds: next, text: null },
      };
    });
  };

  const setText = (question: QuizQuestionForLearner, text: string): void => {
    setAnswers((prev) => ({
      ...prev,
      [question.id]: { questionId: question.id, optionIds: [], text },
    }));
  };

  const retake = (): void => {
    setAnswers({});
    setResult(null);
  };

  const attemptsLabel =
    quiz.attemptLimit === null
      ? `${quiz.attemptsUsed} attempt${quiz.attemptsUsed === 1 ? '' : 's'} so far · unlimited retakes`
      : `${quiz.attemptsUsed} of ${quiz.attemptLimit} attempt${
          quiz.attemptLimit === 1 ? '' : 's'
        } used`;

  return (
    <Card>
      <CardHeader
        title={quiz.title}
        description="Answers are checked against the key for your own feedback. Nothing here is graded, recorded against you, or seen by anyone else."
        actions={<Badge>{attemptsLabel}</Badge>}
      />

      <div className="p-5 flex flex-col gap-5">
        {quiz.descriptionMd ? <Markdown compact>{quiz.descriptionMd}</Markdown> : null}

        {result ? <ScorePanel result={result} /> : null}

        {quiz.questions.length === 0 ? (
          <p className="text-sm text-ink-muted">
            This knowledge check has no questions yet.
          </p>
        ) : (
          <ol className="flex flex-col gap-5">
            {quiz.questions.map((question, index) => (
              <QuestionBlock
                key={question.id}
                index={index}
                question={question}
                answer={answers[question.id]}
                result={resultsByQuestion.get(question.id) ?? null}
                disabled={!!result || submit.isPending}
                onChoose={(optionId) => setChoice(question, optionId)}
                onText={(text) => setText(question, text)}
              />
            ))}
          </ol>
        )}

        <div className="flex flex-wrap items-center gap-3 pt-1">
          {result ? (
            quiz.attemptsRemaining === null || quiz.attemptsRemaining > 1 ? (
              <Button
                variant="secondary"
                icon={<RotateCcw size={15} aria-hidden />}
                onClick={retake}
              >
                Try it again
              </Button>
            ) : (
              <p className="text-sm text-ink-muted">
                That was your last attempt. The answers and explanations above stay on the page.
              </p>
            )
          ) : outOfAttempts ? (
            <Alert tone="warn" title="No attempts left">
              You have used every attempt on this knowledge check. Your previous scores are listed
              below, and nothing about them is reported anywhere.
            </Alert>
          ) : (
            <>
              <Button
                variant="primary"
                onClick={() => submit.mutate()}
                loading={submit.isPending}
                disabled={answeredCount === 0}
              >
                Check my answers
              </Button>
              <p className="text-sm text-ink-subtle">
                {answeredCount} of {quiz.questions.length} answered
                {answeredCount < quiz.questions.length
                  ? ' — unanswered questions are marked incorrect.'
                  : '.'}
              </p>
            </>
          )}
        </div>

        {history.data && history.data.length > 0 ? (
          <div className="border-t border-line pt-4">
            <h3 className="text-sm font-semibold text-ink mb-2">Your previous attempts</h3>
            <ul className="flex flex-col gap-1.5">
              {history.data.map((attempt) => (
                <li
                  key={attempt.attemptNumber}
                  className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-ink-muted"
                >
                  <span className="tabular-nums font-medium text-ink">
                    Attempt {attempt.attemptNumber}
                  </span>
                  <span className="tabular-nums">{pct(attempt.scorePct)}</span>
                  <Badge tone={attempt.passed ? 'ok' : 'neutral'}>
                    {attempt.passed ? 'Above the suggested mark' : 'Below the suggested mark'}
                  </Badge>
                  <span className="text-xs text-ink-subtle">
                    {formatDateTime(attempt.submittedAt)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </Card>
  );
}

function ScorePanel({ result }: { result: QuizAttemptResult }) {
  return (
    <div
      className={clsx(
        'rounded-control border px-4 py-3',
        result.passed ? 'bg-accent-wash border-accent/40' : 'bg-warn-wash border-warn/40',
      )}
      role="status"
    >
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <p className="text-lg font-semibold text-ink tabular-nums">{pct(result.scorePct)}</p>
        <p className="text-sm text-ink-muted tabular-nums">
          {result.pointsEarned} of {result.pointsTotal} points
        </p>
        <Badge tone={result.passed ? 'ok' : 'warn'}>
          {result.passed
            ? `At or above the ${result.passThresholdPct}% the author suggests`
            : `Below the ${result.passThresholdPct}% the author suggests`}
        </Badge>
      </div>
      <p className="text-sm text-ink-muted mt-1.5 leading-relaxed">
        {result.passed
          ? 'That threshold is the author’s rule of thumb for “this has landed”, not a pass mark — nothing about this attempt is recorded as a grade.'
          : 'That threshold is the author’s rule of thumb for “this has landed”, not a pass mark. Read the explanations below and try again — nothing here counts against you.'}
      </p>
      <p className="text-xs text-ink-subtle mt-1.5">
        {result.attemptsRemaining === null
          ? 'You can retake this as often as you like.'
          : `${result.attemptsRemaining} attempt${result.attemptsRemaining === 1 ? '' : 's'} left.`}
      </p>
    </div>
  );
}

function QuestionBlock({
  index,
  question,
  answer,
  result,
  disabled,
  onChoose,
  onText,
}: {
  index: number;
  question: QuizQuestionForLearner;
  answer: QuizAnswer | undefined;
  result: QuizQuestionResult | null;
  disabled: boolean;
  onChoose: (optionId: string) => void;
  onText: (text: string) => void;
}) {
  const groupId = useId();
  const chosen = answer?.optionIds ?? [];
  const isMulti = question.kind === 'multi_select';
  const correctIds = new Set(result?.correctOptionIds ?? []);

  return (
    <li className="border border-line rounded-control p-4">
      <fieldset>
        <legend className="w-full">
          <div className="flex items-start gap-3">
            <span className="text-sm font-semibold text-ink-subtle tabular-nums shrink-0 mt-0.5">
              {index + 1}.
            </span>
            <div className="min-w-0 flex-1">
              <Markdown compact>{question.promptMd}</Markdown>
              <p className="text-xs text-ink-subtle mt-1">
                {isMulti
                  ? 'Choose every option that applies.'
                  : question.kind === 'short_answer'
                    ? 'Type your answer.'
                    : 'Choose one.'}
                {' · '}
                {question.points} point{question.points === 1 ? '' : 's'}
              </p>
            </div>
            {result ? (
              <span
                className={clsx(
                  'inline-flex items-center gap-1 text-xs font-medium shrink-0',
                  result.correct ? 'text-ok' : 'text-danger',
                )}
              >
                {result.correct ? <Check size={14} aria-hidden /> : <X size={14} aria-hidden />}
                {result.correct ? 'Correct' : 'Not quite'}
              </span>
            ) : null}
          </div>
        </legend>

        <div className="mt-3 flex flex-col gap-1.5 pl-7">
          {question.kind === 'short_answer' ? (
            <>
              <label htmlFor={`${groupId}-text`} className="sr-only">
                Your answer
              </label>
              <Input
                id={`${groupId}-text`}
                value={answer?.text ?? ''}
                onChange={(e) => onText(e.target.value)}
                disabled={disabled}
                autoComplete="off"
              />
            </>
          ) : (
            question.options.map((option) => {
              const selected = chosen.includes(option.id);
              const isCorrect = result ? correctIds.has(option.id) : false;
              return (
                <label
                  key={option.id}
                  className={clsx(
                    'flex items-start gap-2.5 rounded-control border px-3 py-2 text-sm cursor-pointer',
                    disabled && 'cursor-default',
                    result && isCorrect
                      ? 'border-accent/50 bg-accent-wash'
                      : result && selected && !isCorrect
                        ? 'border-danger/50 bg-danger-wash'
                        : 'border-line hover:bg-surface-2',
                  )}
                >
                  <input
                    type={isMulti ? 'checkbox' : 'radio'}
                    name={groupId}
                    checked={selected}
                    disabled={disabled}
                    onChange={() => onChoose(option.id)}
                    className="mt-0.5 h-4 w-4 shrink-0"
                  />
                  <span className="min-w-0 text-ink">{option.label}</span>
                  {result && isCorrect ? (
                    <span className="ml-auto text-xs font-medium text-ok shrink-0">
                      Correct answer
                    </span>
                  ) : null}
                  {result && selected && !isCorrect ? (
                    <span className="ml-auto text-xs font-medium text-danger shrink-0">
                      You chose this
                    </span>
                  ) : null}
                </label>
              );
            })
          )}
        </div>

        {result?.explanationMd ? (
          <div className="mt-3 ml-7 border-l-2 border-line-strong pl-3">
            <p className="eyebrow mb-1">
              Why
            </p>
            <Markdown compact>{result.explanationMd}</Markdown>
          </div>
        ) : null}
      </fieldset>
    </li>
  );
}
